import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { rollout } from "../constants/rollout";
import {
  followButtonBlue,
  followButtonBlueHover,
} from "../constants/brandColors";
import { uiNeutrals } from "../constants/uiNeutrals";
import {
  DEALS_HEADER_TOP_GAP_PX,
  DEALS_HEADER_ROW_MIN_HEIGHT_PX,
  DEALS_HEADER_BOTTOM_PADDING_PX,
  dealsScrollContentPaddingTop,
} from "../constants/dealsShellLayout";
import { useRequireAuth } from "../hooks/useRequireAuth";
import { useAuth } from "../contexts/AuthContext";
import {
  followBrand,
  unfollowBrand,
  getFollowedBrands,
  isBrandFollowed,
} from "../utils/userPreferences";
import { useRestaurantDealsBySlug } from "../hooks/useRestaurantDealsBySlug";
import DealDetails from "../components/deals/DealDetails";
import { DealListRow } from "../components/deals/dealList/components/DealListRow";
import RestaurantItemImage from "../components/restaurant/RestaurantItemImage";
import RestaurantDealCountPills from "../components/deals/RestaurantDealCountPills";
import { formatBrandCuisineLine } from "../utils/formatBrandCuisineLine";

const YoursRestaurantDealsPage = () => {
  const { restaurantName } = useParams();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const { isAuthenticated } = useAuth();
  const { deals, loading, restaurantInfo, activeDeals, inactiveDeals } =
    useRestaurantDealsBySlug(restaurantName);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);

  const n = uiNeutrals;

  useEffect(() => {
    if (restaurantInfo?._id && isAuthenticated) {
      const checkFollowState = async () => {
        await getFollowedBrands();
        setIsFollowing(isBrandFollowed(restaurantInfo._id));
      };
      checkFollowState();
    } else {
      setIsFollowing(false);
    }
  }, [restaurantInfo?._id, isAuthenticated]);

  const handleFollowToggle = async () => {
    if (!restaurantInfo?._id) return;
    if (isFollowing) {
      const success = await unfollowBrand(restaurantInfo._id);
      if (success) {
        setIsFollowing(false);
        window.dispatchEvent(new Event("followedBrandsChanged"));
      }
      return;
    }
    requireAuth(async () => {
      const success = await followBrand(restaurantInfo._id);
      if (success) {
        setIsFollowing(true);
        window.dispatchEvent(new Event("followedBrandsChanged"));
      }
    });
  };

  const goBack = () => {
    navigate("/deals");
  };

  const cuisineLine = restaurantInfo
    ? formatBrandCuisineLine(restaurantInfo.cuisine ?? restaurantInfo.category)
    : null;

  const headerBackButtonSx = {
    width: 40,
    height: 40,
    p: 0,
    bgcolor: n.frostedFill,
    backdropFilter: "blur(18px) saturate(1.05)",
    WebkitBackdropFilter: "blur(18px) saturate(1.05)",
    border: n.frostedToggleBorder,
    borderRadius: "50%",
    color: "#000",
    "&:hover": { bgcolor: n.frostedFillHover },
  };

  if (loading) {
    return (
      <Box
        sx={{
          pt: dealsScrollContentPaddingTop,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#000" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          pt: `calc(env(safe-area-inset-top, 0px) + ${DEALS_HEADER_TOP_GAP_PX}px)`,
          pb: `${DEALS_HEADER_BOTTOM_PADDING_PX}px`,
          bgcolor: "background.default",
          borderBottom: `1px solid ${n.rowDivider}`,
        }}
      >
        <Box
          sx={{
            minHeight: DEALS_HEADER_ROW_MIN_HEIGHT_PX,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            gap: 1,
          }}
        >
          <IconButton
            onClick={goBack}
            aria-label="Back"
            sx={headerBackButtonSx}
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {rollout.enableFollowing && restaurantInfo?._id ? (
              <Button
                variant="contained"
                size="small"
                disableRipple
                disableElevation
                onClick={handleFollowToggle}
                sx={{
                  minWidth: 80,
                  px: 1.5,
                  py: 1,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "25px",
                  boxShadow: "none",
                  lineHeight: 1.2,
                  minHeight: 30,
                  ...(isFollowing
                    ? {
                        bgcolor: n.followingControl,
                        color: "#000",
                        "&:hover": { bgcolor: n.followingControlHover },
                      }
                    : {
                        bgcolor: followButtonBlue,
                        color: "#fff",
                        "&:hover": {
                          bgcolor: followButtonBlueHover,
                          borderColor: "rgba(0, 0, 0, 0.08)",
                        },
                      }),
                }}
              >
                {isFollowing ? "Following" : "Follow"}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          minHeight: 0,
          pt: dealsScrollContentPaddingTop,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        <Box sx={{ maxWidth: 720, mx: "auto", px: 2, pb: 4, pt: 2 }}>
          {restaurantInfo && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  bgcolor: "background.default",
                  border: `1px solid ${n.wellBorder}`,
                }}
              >
                <RestaurantItemImage
                  restaurantName={restaurantInfo.name}
                  imageUrl={restaurantInfo.logoUrl}
                  useDefaultFallback
                  size="100%"
                  borderRadius="50%"
                  loading="eager"
                  sx={{ width: "100%", height: "100%" }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "1rem" }} noWrap>
                  {restaurantInfo.name}
                </Typography>
                {cuisineLine ? (
                  <Typography
                    sx={{ fontSize: "0.8rem", color: "#888", mt: 0.35 }}
                    noWrap
                    title={cuisineLine}
                  >
                    {cuisineLine}
                  </Typography>
                ) : null}
                {(activeDeals.length > 0 || inactiveDeals.length > 0) && (
                  <Box sx={{ mt: 1.25 }}>
                    <RestaurantDealCountPills
                      activeCount={activeDeals.length}
                      inactiveCount={inactiveDeals.length}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {activeDeals.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "#888",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Active
              </Typography>
              <Box>
                {activeDeals.map((deal, index) => (
                  <DealListRow
                    key={deal._id}
                    deal={deal}
                    index={index}
                    compact
                    onExpandDeal={setExpandedDeal}
                  />
                ))}
              </Box>
            </>
          )}

          {inactiveDeals.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "#888",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Inactive
              </Typography>
              <Box>
                {inactiveDeals.map((deal, index) => (
                  <DealListRow
                    key={deal._id}
                    deal={deal}
                    index={index}
                    compact
                    onExpandDeal={setExpandedDeal}
                  />
                ))}
              </Box>
            </Box>
          )}

          {deals.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No deals found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No deals available for this restaurant.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <DealDetails
        deal={expandedDeal}
        isOpen={Boolean(expandedDeal)}
        onClose={() => setExpandedDeal(null)}
      />
    </Box>
  );
};

export default YoursRestaurantDealsPage;
